import {
  ScopeResponse,
  ScopeSectionCreateRequest,
  ScopeSectionResponse,
  ScopeSectionRuleModeName,
  ScopeSectionRuleRequest,
  ScopeSectionRuleTypeName,
} from '@ydistri/api-sdk';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getRequestErrorMessage } from '../../../lib/utils/getRequestErrorMessage';
import {
  areScopeRuleRequestsEqual,
  ScopeDraft,
  ScopeSectionDraft,
  toInitialScopeDraft,
  toScopeDraft,
  toScopeSectionRuleRequest,
} from './scopeEditorModel';

interface UseScopeEditorPersistenceProps {
  selection: ScopeResponse;
  onCreateSection: (request: ScopeSectionCreateRequest) => Promise<ScopeSectionResponse>;
  onDeleteRule: (
    scopeSectionId: number,
    scopeSectionRuleTypeId: ScopeSectionRuleTypeName,
  ) => Promise<void>;
  onDeleteSection: (scopeSectionId: number) => Promise<void>;
  onUpsertRule: (scopeSectionId: number, request: ScopeSectionRuleRequest) => Promise<void>;
}

interface ScopeEditorPersistence {
  draft: ScopeDraft;
  isAutoSaving: boolean;
  submitError?: string;
  setSubmitError: React.Dispatch<React.SetStateAction<string | undefined>>;
  updateSections: (sections: ScopeSectionDraft[]) => void;
}

const persistedSectionId = (sectionId: string): number | undefined => {
  if (!/^\d+$/.test(sectionId)) return undefined;

  const parsedId = Number(sectionId);
  return Number.isSafeInteger(parsedId) && parsedId > 0 ? parsedId : undefined;
};

const ruleKey = (scopeSectionId: number, ruleType: ScopeSectionRuleTypeName): string =>
  `${scopeSectionId}:${ruleType}`;

const sectionRuleRequests = (selection: ScopeResponse): Map<string, ScopeSectionRuleRequest> =>
  new Map(
    selection.sections.flatMap(section =>
      section.rules.map(rule => [
        ruleKey(section.scopeSectionId, rule.scopeSectionRuleTypeId),
        {
          scopeSectionRuleTypeId: rule.scopeSectionRuleTypeId,
          scopeSectionRuleModeId: rule.scopeSectionRuleModeId,
          ids: rule.ids,
        },
      ]),
    ),
  );

const sectionEquivalent = (left: ScopeSectionDraft, right: ScopeSectionDraft): boolean => {
  if (left.rules.length !== right.rules.length) return false;

  const rightRules = new Map(right.rules.map(rule => [rule.type, rule]));
  return left.rules.every(rule => {
    const rightRule = rightRules.get(rule.type);
    return (
      rightRule !== undefined &&
      areScopeRuleRequestsEqual(
        toScopeSectionRuleRequest(rule),
        toScopeSectionRuleRequest(rightRule),
      )
    );
  });
};

const useScopeEditorPersistence = ({
  selection,
  onCreateSection,
  onDeleteRule,
  onDeleteSection,
  onUpsertRule,
}: UseScopeEditorPersistenceProps): ScopeEditorPersistence => {
  const [draft, setDraft] = useState<ScopeDraft>(() => toInitialScopeDraft(selection));
  const [pendingOperationCount, setPendingOperationCount] = useState(0);
  const [submitError, setSubmitError] = useState<string>();
  const draftRef = useRef(draft);
  const selectionScopeIdRef = useRef(selection.scopeId);
  const generationRef = useRef(0);
  const operationQueueRef = useRef<Promise<void>>(Promise.resolve());
  const sectionTimersRef = useRef(new Map<string, number>());
  const sectionAliasesRef = useRef(new Map<string, number>());
  const persistedSectionIdsRef = useRef(
    new Set(selection.sections.map(section => section.scopeSectionId)),
  );
  const serverRuleRequestsRef = useRef(sectionRuleRequests(selection));
  const dirtySectionIdsRef = useRef(new Set<string>());
  const scheduledSectionCreatesRef = useRef(new Set<string>());
  const removedTemporarySectionsRef = useRef(new Set<string>());

  const clearSectionTimers = useCallback(() => {
    sectionTimersRef.current.forEach(timerId => window.clearTimeout(timerId));
    sectionTimersRef.current.clear();
  }, []);

  useEffect(() => () => clearSectionTimers(), [clearSectionTimers]);

  const resetEditor = useCallback(
    (nextSelection: ScopeResponse) => {
      generationRef.current += 1;
      clearSectionTimers();
      operationQueueRef.current = Promise.resolve();
      sectionAliasesRef.current.clear();
      persistedSectionIdsRef.current = new Set(
        nextSelection.sections.map(section => section.scopeSectionId),
      );
      serverRuleRequestsRef.current = sectionRuleRequests(nextSelection);
      dirtySectionIdsRef.current.clear();
      scheduledSectionCreatesRef.current.clear();
      removedTemporarySectionsRef.current.clear();
      selectionScopeIdRef.current = nextSelection.scopeId;
      const nextDraft = toInitialScopeDraft(nextSelection);
      draftRef.current = nextDraft;
      setDraft(nextDraft);
      setPendingOperationCount(0);
      setSubmitError(undefined);
    },
    [clearSectionTimers],
  );

  useEffect(() => {
    if (selection.scopeId !== selectionScopeIdRef.current) resetEditor(selection);
  }, [resetEditor, selection]);

  const enqueueOperation = useCallback((operation: () => Promise<void>): void => {
    const operationGeneration = generationRef.current;
    setPendingOperationCount(count => count + 1);
    setSubmitError(undefined);

    const result = operationQueueRef.current.then(async () => {
      if (generationRef.current !== operationGeneration) return;
      await operation();
    });

    operationQueueRef.current = result.catch(() => undefined);
    void result
      .catch((error: unknown) => {
        if (generationRef.current === operationGeneration) {
          setSubmitError(getRequestErrorMessage(error, 'The Scope could not be saved.'));
        }
      })
      .finally(() => {
        if (generationRef.current === operationGeneration) {
          setPendingOperationCount(count => Math.max(0, count - 1));
        }
      });
  }, []);

  const resolveSectionId = useCallback((sectionId: string): number | undefined => {
    const alias = sectionAliasesRef.current.get(sectionId);
    if (alias !== undefined) return alias;

    const parsedId = persistedSectionId(sectionId);
    return parsedId !== undefined && persistedSectionIdsRef.current.has(parsedId)
      ? parsedId
      : undefined;
  }, []);

  const findDraftSection = useCallback(
    (scopeSectionId: number): ScopeSectionDraft | undefined =>
      draftRef.current.sections.find(
        section =>
          section.id === scopeSectionId.toString() ||
          sectionAliasesRef.current.get(section.id) === scopeSectionId,
      ),
    [],
  );

  const reconcilePersistedSection = useCallback(
    async (scopeSectionId: number): Promise<void> => {
      const section = findDraftSection(scopeSectionId);
      if (!section) return;

      const desiredRequests = section.rules
        .filter(rule => rule.valueIds.length > 0)
        .map(toScopeSectionRuleRequest);
      const desiredRuleTypes = new Set(
        desiredRequests.map(request => request.scopeSectionRuleTypeId),
      );
      const includeRequests = desiredRequests.filter(
        request => request.scopeSectionRuleModeId === ScopeSectionRuleModeName.Include,
      );
      const otherRequests = desiredRequests.filter(
        request => request.scopeSectionRuleModeId !== ScopeSectionRuleModeName.Include,
      );

      const upsert = async (request: ScopeSectionRuleRequest): Promise<void> => {
        const key = ruleKey(scopeSectionId, request.scopeSectionRuleTypeId);
        const persistedRequest = serverRuleRequestsRef.current.get(key);
        if (persistedRequest && areScopeRuleRequestsEqual(persistedRequest, request)) return;

        await onUpsertRule(scopeSectionId, request);
        serverRuleRequestsRef.current.set(key, request);
      };

      for (const request of includeRequests) await upsert(request);

      for (const request of otherRequests) {
        await upsert(request);
      }

      const persistedRules = [...serverRuleRequestsRef.current.entries()].filter(([key]) =>
        key.startsWith(`${scopeSectionId}:`),
      );
      for (const [key, persistedRequest] of persistedRules) {
        if (desiredRuleTypes.has(persistedRequest.scopeSectionRuleTypeId)) continue;

        await onDeleteRule(scopeSectionId, persistedRequest.scopeSectionRuleTypeId);
        serverRuleRequestsRef.current.delete(key);
      }
    },
    [findDraftSection, onDeleteRule, onUpsertRule],
  );

  const reconcileTemporarySection = useCallback(
    async (temporarySectionId: string): Promise<void> => {
      if (scheduledSectionCreatesRef.current.has(temporarySectionId)) return;
      const section = draftRef.current.sections.find(item => item.id === temporarySectionId);
      const initialRule = section?.rules.find(
        rule => rule.mode === ScopeSectionRuleModeName.Include && rule.valueIds.length > 0,
      );
      if (!section || !initialRule) return;

      scheduledSectionCreatesRef.current.add(temporarySectionId);
      try {
        const createdSection = await onCreateSection(toScopeSectionRuleRequest(initialRule));
        const scopeSectionId = createdSection.scopeSectionId;
        sectionAliasesRef.current.set(temporarySectionId, scopeSectionId);
        persistedSectionIdsRef.current.add(scopeSectionId);
        for (const rule of createdSection.rules) {
          serverRuleRequestsRef.current.set(ruleKey(scopeSectionId, rule.scopeSectionRuleTypeId), {
            scopeSectionRuleTypeId: rule.scopeSectionRuleTypeId,
            scopeSectionRuleModeId: rule.scopeSectionRuleModeId,
            ids: rule.ids,
          });
        }

        dirtySectionIdsRef.current.delete(temporarySectionId);
        dirtySectionIdsRef.current.add(scopeSectionId.toString());
        const currentDraft = draftRef.current;
        const currentSection = currentDraft.sections.find(item => item.id === temporarySectionId);
        if (currentSection) {
          const createdRuleIds = new Map(
            createdSection.rules.map(rule => [
              rule.scopeSectionRuleTypeId,
              rule.scopeSectionRuleId.toString(),
            ]),
          );
          const nextDraft: ScopeDraft = {
            ...currentDraft,
            sections: currentDraft.sections.map(item =>
              item.id === temporarySectionId
                ? {
                    ...item,
                    id: scopeSectionId.toString(),
                    rules: item.rules.map(rule => ({
                      ...rule,
                      id: createdRuleIds.get(rule.type) ?? rule.id,
                    })),
                  }
                : item,
            ),
          };
          draftRef.current = nextDraft;
          setDraft(nextDraft);
        }

        if (
          removedTemporarySectionsRef.current.has(temporarySectionId) ||
          !draftRef.current.sections.some(item => item.id === scopeSectionId.toString())
        ) {
          await onDeleteSection(scopeSectionId);
          persistedSectionIdsRef.current.delete(scopeSectionId);
          for (const key of serverRuleRequestsRef.current.keys()) {
            if (key.startsWith(`${scopeSectionId}:`)) serverRuleRequestsRef.current.delete(key);
          }
          return;
        }

        await reconcilePersistedSection(scopeSectionId);
      } finally {
        scheduledSectionCreatesRef.current.delete(temporarySectionId);
        removedTemporarySectionsRef.current.delete(temporarySectionId);
      }
    },
    [onCreateSection, onDeleteSection, reconcilePersistedSection],
  );

  const scheduleSectionReconciliation = useCallback(
    (sectionId: string): void => {
      const currentTimer = sectionTimersRef.current.get(sectionId);
      if (currentTimer !== undefined) window.clearTimeout(currentTimer);

      const timerId = window.setTimeout(() => {
        sectionTimersRef.current.delete(sectionId);
        enqueueOperation(() => {
          const resolvedSectionId = resolveSectionId(sectionId);
          return resolvedSectionId === undefined
            ? reconcileTemporarySection(sectionId)
            : reconcilePersistedSection(resolvedSectionId);
        });
      }, 400);
      sectionTimersRef.current.set(sectionId, timerId);
    },
    [enqueueOperation, reconcilePersistedSection, reconcileTemporarySection, resolveSectionId],
  );

  const removeSection = useCallback(
    (removedSection: ScopeSectionDraft): void => {
      const timerId = sectionTimersRef.current.get(removedSection.id);
      if (timerId !== undefined) window.clearTimeout(timerId);
      sectionTimersRef.current.delete(removedSection.id);

      const scopeSectionId = resolveSectionId(removedSection.id);
      if (scopeSectionId === undefined) {
        if (scheduledSectionCreatesRef.current.has(removedSection.id)) {
          removedTemporarySectionsRef.current.add(removedSection.id);
        }
        return;
      }

      enqueueOperation(async () => {
        try {
          await onDeleteSection(scopeSectionId);
          persistedSectionIdsRef.current.delete(scopeSectionId);
          for (const key of serverRuleRequestsRef.current.keys()) {
            if (key.startsWith(`${scopeSectionId}:`)) serverRuleRequestsRef.current.delete(key);
          }
        } catch (error) {
          if (!draftRef.current.sections.some(section => section.id === removedSection.id)) {
            const restoredDraft = {
              ...draftRef.current,
              sections: [...draftRef.current.sections, removedSection],
            };
            draftRef.current = restoredDraft;
            setDraft(restoredDraft);
          }
          throw error;
        }
      });
    },
    [enqueueOperation, onDeleteSection, resolveSectionId],
  );

  const updateSections = useCallback(
    (sections: ScopeSectionDraft[]): void => {
      const previousDraft = draftRef.current;
      const nextSectionIds = new Set(sections.map(section => section.id));
      const removedSections = previousDraft.sections.filter(
        section => !nextSectionIds.has(section.id),
      );
      const previousSections = new Map(
        previousDraft.sections.map(section => [section.id, section]),
      );
      const changedSections = sections.filter(section => {
        const previousSection = previousSections.get(section.id);
        return !previousSection || !sectionEquivalent(previousSection, section);
      });

      const nextDraft = { ...previousDraft, sections };
      draftRef.current = nextDraft;
      setDraft(nextDraft);
      setSubmitError(undefined);

      for (const removedSection of removedSections) {
        dirtySectionIdsRef.current.add(removedSection.id);
        removeSection(removedSection);
      }
      for (const changedSection of changedSections) {
        dirtySectionIdsRef.current.add(changedSection.id);
        scheduleSectionReconciliation(changedSection.id);
      }
    },
    [removeSection, scheduleSectionReconciliation],
  );

  useEffect(() => {
    if (
      selection.scopeId !== selectionScopeIdRef.current ||
      pendingOperationCount > 0 ||
      sectionTimersRef.current.size > 0
    ) {
      return;
    }

    const serverDraft = toScopeDraft(selection);
    const localSections = new Map(draftRef.current.sections.map(section => [section.id, section]));
    const serverSectionIds = new Set(serverDraft.sections.map(section => section.id));
    const mergedSections: ScopeSectionDraft[] = [];

    persistedSectionIdsRef.current = new Set(
      selection.sections.map(section => section.scopeSectionId),
    );
    serverRuleRequestsRef.current = sectionRuleRequests(selection);

    for (const serverSection of serverDraft.sections) {
      const localSection = localSections.get(serverSection.id);
      if (!dirtySectionIdsRef.current.has(serverSection.id)) {
        mergedSections.push(serverSection);
      } else if (localSection && sectionEquivalent(localSection, serverSection)) {
        dirtySectionIdsRef.current.delete(serverSection.id);
        mergedSections.push(serverSection);
      } else if (localSection) {
        mergedSections.push(localSection);
      }
    }

    for (const localSection of draftRef.current.sections) {
      if (serverSectionIds.has(localSection.id)) continue;

      if (persistedSectionId(localSection.id) === undefined) {
        mergedSections.push(localSection);
      } else if (dirtySectionIdsRef.current.has(localSection.id)) {
        mergedSections.push(localSection);
      }
    }

    for (const dirtySectionId of dirtySectionIdsRef.current) {
      if (!localSections.has(dirtySectionId) && !serverSectionIds.has(dirtySectionId)) {
        dirtySectionIdsRef.current.delete(dirtySectionId);
      }
    }

    const mergedDraft = { ...serverDraft, sections: mergedSections };
    draftRef.current = mergedDraft;
    setDraft(mergedDraft);
  }, [pendingOperationCount, selection]);

  return {
    draft,
    isAutoSaving: pendingOperationCount > 0,
    submitError,
    setSubmitError,
    updateSections,
  };
};

export default useScopeEditorPersistence;
